import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { type Enum } from '@webapp/infrastructure/types/Enum'
import {
  type ConversationComponentTypes,
  type ConversationDetailBlockTypes,
} from '../../value-objects/enums'

export const ConversationStreamEventTypes = {
  CONVERSATION_START: 'aios:conversation.start',
  SESSION_START: 'intake-processing:session.start',
  CONTEXT_PRELOADED: 'intake-processing:context.preloaded',
  THINKING: 'intake-processing:thinking',
  TOOL_CALL: 'intake-processing:tool.call',
  SPECIALIST_INVOKE_START: 'intake-processing:specialist.invoke.start',
  SPECIALIST_INVOKE_COMPLETE: 'intake-processing:specialist.invoke.complete',
  COUNTER_BUMPED: 'intake-processing:counter.bumped',
  UI_UPDATE: 'intake-processing:ui.update',
  STAGE_TRANSITION: 'intake-processing:stage.transition',
  MESSAGE_FINAL: 'intake-processing:message.final',
  OUT_OF_SCOPE: 'intake-processing:out_of_scope',
  ERROR: 'intake-processing:error',
  CONVERSATION_END: 'aios:conversation.end',
} as const

export type ConversationStreamEventTypeEnum = Enum<
  typeof ConversationStreamEventTypes
>

export type ConversationChoiceOptionApiModel = {
  label: string
  value: string
  message: string
}

export type ConversationMessageComponentApiModel = {
  type: typeof ConversationComponentTypes.MESSAGE
  params: {
    sender: string
    content: string
    tags?: string[]
  }
}

export type ConversationConfirmationOptionsComponentApiModel = {
  type: typeof ConversationComponentTypes.CONFIRMATION_OPTIONS
  params: {
    options: ConversationChoiceOptionApiModel[]
  }
}

export type ConversationGroupedDropdownOptionApiModel = {
  id: number | string
  name: string
}

export type ConversationGroupedDropdownGroupApiModel = {
  id: number | string
  name: string
  options?: ConversationGroupedDropdownOptionApiModel[]
}

export type ConversationGroupedDropdownComponentApiModel = {
  type: typeof ConversationComponentTypes.GROUPED_DROPDOWN
  params: {
    groups: ConversationGroupedDropdownGroupApiModel[]
    group_label: string
    option_label?: string
    helper_text?: string
    default_group_id?: number | string
    default_option_id?: number | string
  }
}

export type ConversationCartPriceApiModel = {
  price: number
  currency: CurrencyName
}

export type ConversationCatalogItemPriceApiModel = {
  price: number
  currency: CurrencyName
}

export type ConversationCartItemApiModel = {
  line_id: string
  img?: string
  title: string
  number_of_items: number
  price: ConversationCartPriceApiModel
  selected_options?: Record<string, unknown>
}

export type ConversationCartActionApiModel = {
  text: string
  icon?: string
}

export type ConversationCartComponentApiModel = {
  type: typeof ConversationComponentTypes.CART
  params: {
    cart_id: string
    version: number
    total_items: number
    estimated_total: ConversationCartPriceApiModel
    items: ConversationCartItemApiModel[]
    vendor?: ConversationVendorApiModel
    total_label?: string
    continue_action?: ConversationCartActionApiModel
    disclaimer?: string
  }
}

export type ConversationCatalogItemTagApiModel = {
  img?: string
  icon?: string
  variant?: string
  text: string
}

export type ConversationCatalogItemMoreInformationApiModel = {
  images?: string[]
  agent_analysis?: {
    title?: string
    body?: string
    quick_replies?: string[]
  }
  specification?: [string, string][]
  estimated_total?: ConversationCatalogItemPriceApiModel
  quantity?: number
  rating?: number
  price?: {
    current_price?: ConversationCatalogItemPriceApiModel
    old_price?: ConversationCatalogItemPriceApiModel
    deal_tag?: string
    volume_pricing?: ConversationVolumePricingApiModel
  }
  ratings_and_review?: {
    rating?: number
    reviews?: unknown[]
  }
  in_stock?: boolean
  detail_sections?: ConversationDetailSectionApiModel[]
  delivery?: ConversationCatalogItemDeliveryApiModel
}

export type ConversationVendorApiModel = {
  id: string
  name: string
  logo_url?: string
}

export type ConversationCatalogItemRatingApiModel = {
  value: number
  count: number
  scale_max?: number
}

export type ConversationCatalogItemVendorBadgeApiModel = {
  slot: string
  text: string
  icon?: string
  logo_url?: string
}

export type ConversationCatalogItemDeliveryApiModel = {
  label: string
}

export type ConversationDetailTextBlockApiModel = {
  type: typeof ConversationDetailBlockTypes.TEXT
  content?: string
  variant?: string
}

export type ConversationDetailKeyValueBlockApiModel = {
  type: typeof ConversationDetailBlockTypes.KEY_VALUE
  rows?: { label?: string; value?: string }[]
  collapsed_after?: number
}

export type ConversationDetailTableBlockApiModel = {
  type: typeof ConversationDetailBlockTypes.TABLE
  columns?: { label?: string; align?: string }[]
  rows?: string[][]
}

export type ConversationDetailListBlockApiModel = {
  type: typeof ConversationDetailBlockTypes.LIST
  items?: { icon?: string; title?: string; body?: string }[]
}

export type ConversationDetailChipsBlockApiModel = {
  type: typeof ConversationDetailBlockTypes.CHIPS
  options?: { label?: string; message?: string }[]
}

export type ConversationDetailBlockApiModel =
  | ConversationDetailTextBlockApiModel
  | ConversationDetailKeyValueBlockApiModel
  | ConversationDetailTableBlockApiModel
  | ConversationDetailListBlockApiModel
  | ConversationDetailChipsBlockApiModel

export type ConversationDetailSectionApiModel = {
  id?: string
  title?: string
  icon?: string
  variant?: string
  tags?: ConversationCatalogItemTagApiModel[]
  blocks?: ConversationDetailBlockApiModel[]
}

export type ConversationVolumePricingSavingsHintApiModel = {
  savings_pct?: number
  min_qty?: number
  label?: string
}

export type ConversationVolumePricingTierApiModel = {
  label?: string
  min_qty?: number
  max_qty?: number
  unit_price?: ConversationCatalogItemPriceApiModel
  savings_pct?: number
}

export type ConversationVolumePricingApiModel = {
  savings_hint?: ConversationVolumePricingSavingsHintApiModel
  tiers?: ConversationVolumePricingTierApiModel[]
}

export type ConversationCatalogItemPriceParamsApiModel = {
  price?: number
  currency?: CurrencyName
  current_price?: ConversationCatalogItemPriceApiModel
  old_price?: ConversationCatalogItemPriceApiModel
  deal_tag?: string
  volume_pricing?: ConversationVolumePricingApiModel
}

export type ConversationCatalogItemComponentApiModel = {
  type: typeof ConversationComponentTypes.CATALOG_ITEM
  params: {
    img?: string
    title: string
    body: string
    tags: ConversationCatalogItemTagApiModel[]
    price: ConversationCatalogItemPriceParamsApiModel
    more_information?: ConversationCatalogItemMoreInformationApiModel
    vendor?: ConversationVendorApiModel
    rating?: ConversationCatalogItemRatingApiModel
    vendor_badges?: ConversationCatalogItemVendorBadgeApiModel[]
    delivery?: ConversationCatalogItemDeliveryApiModel
    bulk_pricing_hint?: string
  }
}

export type ConversationTextComponentApiModel = {
  type: typeof ConversationComponentTypes.TEXT
  params: {
    variant?: string
    text?: string
    content?: string
  }
}

export type ConversationOrderConfirmationComponentApiModel = {
  type: typeof ConversationComponentTypes.ORDER_CONFIRMATION
  params: {
    vendor_redirect_url?: string
  }
}

export type ConversationComponentApiModel =
  | ConversationMessageComponentApiModel
  | ConversationConfirmationOptionsComponentApiModel
  | ConversationGroupedDropdownComponentApiModel
  | ConversationCartComponentApiModel
  | ConversationCatalogItemComponentApiModel
  | ConversationTextComponentApiModel
  | ConversationOrderConfirmationComponentApiModel

export type ConversationEnvelopeApiModel = {
  id: string
  components: ConversationComponentApiModel[]
}

type ConversationEventBase<TType extends string, TData> = {
  type: TType
  data: TData
  version?: string
  timestamp?: string
}

export type ConversationStartEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.CONVERSATION_START,
  {
    conversation_id: string
    session_id: string
    new_conversation: boolean
  }
>

export type ConversationSessionStartEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.SESSION_START,
  {
    session_id: string
    user_id: string
    tenant_id: string
    query: string
    conversation_id: string
  }
>

export type ConversationContextPreloadedEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.CONTEXT_PRELOADED,
  { session_id: string; keys: string[]; conversation_id: string }
>

export type ConversationThinkingEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.THINKING,
  {
    session_id: string
    stage: string
    code: string
    label: string
  }
>

export type ConversationToolCallEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.TOOL_CALL,
  {
    session_id: string
    tool_name: string
    args_summary: Record<string, unknown>
    conversation_id: string
  }
>

export type ConversationSpecialistInvokeStartEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.SPECIALIST_INVOKE_START,
  {
    session_id: string
    specialist: string
    args_summary: Record<string, unknown>
    conversation_id: string
  }
>

export type ConversationSpecialistInvokeCompleteEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.SPECIALIST_INVOKE_COMPLETE,
  {
    session_id: string
    specialist: string
    output_key: string
    result_summary: Record<string, unknown>
    conversation_id: string
  }
>

export type ConversationCounterBumpedEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.COUNTER_BUMPED,
  {
    session_id: string
    questions_asked_this_session: number
    cap: number
    conversation_id: string
  }
>

export type ConversationUiUpdateEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.UI_UPDATE,
  {
    session_id: string
    envelope: ConversationEnvelopeApiModel
    conversation_id: string
  }
>

export type ConversationStageTransitionEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.STAGE_TRANSITION,
  {
    session_id: string
    from_stage: Nullable<string>
    to_stage: string
    conversation_id: string
  }
>

export type ConversationMessageFinalEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.MESSAGE_FINAL,
  {
    session_id: string
    stage: string
    envelope: ConversationEnvelopeApiModel
    conversation_id: string
  }
>

export type ConversationOutOfScopeEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.OUT_OF_SCOPE,
  {
    session_id: string
    stage: string
    cart_state_preserved: boolean
    conversation_id: string
  }
>

export type ConversationErrorEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.ERROR,
  {
    session_id?: string
    code: string
    message: string
    conversation_id?: string
  }
>

export type ConversationEndEvent = ConversationEventBase<
  typeof ConversationStreamEventTypes.CONVERSATION_END,
  {
    conversation_id: string
    status: string
    turn_duration_ms?: number
  }
>

export type ConversationStreamEventApiModel =
  | ConversationStartEvent
  | ConversationSessionStartEvent
  | ConversationContextPreloadedEvent
  | ConversationThinkingEvent
  | ConversationToolCallEvent
  | ConversationSpecialistInvokeStartEvent
  | ConversationSpecialistInvokeCompleteEvent
  | ConversationCounterBumpedEvent
  | ConversationUiUpdateEvent
  | ConversationStageTransitionEvent
  | ConversationMessageFinalEvent
  | ConversationOutOfScopeEvent
  | ConversationErrorEvent
  | ConversationEndEvent

export type ConversationEventMap = {
  message: ConversationStreamEventApiModel
}
